import { FC, useState } from 'react'

import {
  downloadPublicCertificate,
  isSessionEligibleForCertificate,
} from 'components/contentspace/api'
import type { IContentSpaceSession } from 'components/contentspace/types'
import { formatScoreDisplay, normalizeScore } from 'components/contentspace/utils/score'
import Button from 'components/uiKit/Button'
import Icon from 'components/uiKit/Icon'
import Link from 'components/uiKit/Link'
import { notify } from 'components/uiKit/Notification'
import { NotificationType } from 'components/uiKit/Notification/types'
import Progress, { ProgressType } from 'components/uiKit/Progress'
import { t } from 'services/Translation'

interface ICompletionDialogProps {
  session: IContentSpaceSession
  courseName: string
  portalLandingUrl: string
  onClose: () => void
}

// Status palette comes from the brand theme's feedback colors. They're pastel
// (designed as soft notification backgrounds), so we pair them with the brand
// primary text color for the icon/label foreground — checked for legibility on
// pastel fills. Unknown status falls back to neutral chip.
const getStatusInfo = (status: IContentSpaceSession['status']) => {
  switch (status) {
    case 'PASSED':
      return {
        label: 'Passed',
        color: 'var(--brand-successColor)',
        icon: 'statusSuccess20Px' as const,
      }
    case 'COMPLETED':
      return {
        label: 'Completed',
        color: 'var(--brand-infoColor)',
        icon: 'checkmarkCircle1' as const,
      }
    case 'FAILED':
      return {
        label: 'Failed',
        color: 'var(--brand-errorColor)',
        icon: 'statusFail20Px' as const,
      }
    default:
      return {
        label: status,
        color: 'var(--brand-neutralColor)',
        icon: 'alertInfo' as const,
      }
  }
}

export const CompletionDialog: FC<ICompletionDialogProps> = ({
  session,
  courseName,
  portalLandingUrl,
  onClose,
}) => {
  const [downloadingCertificate, setDownloadingCertificate] = useState(false)

  const statusInfo = getStatusInfo(session.status)
  const hasScore = session.score !== null && session.score !== undefined
  const canDownloadCertificate =
    isSessionEligibleForCertificate(session.status) && Boolean(session.sessionToken)

  const handleDownloadCertificate = async () => {
    if (!session.sessionToken) {
      return
    }
    setDownloadingCertificate(true)
    try {
      await downloadPublicCertificate(session.id, session.sessionToken)
    } catch (err) {
      console.error('Failed to download certificate', err)
      notify({
        type: NotificationType.error,
        message: t('contentSpace.public.completion.downloadCertificateError'),
      })
    } finally {
      setDownloadingCertificate(false)
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        // Modal scrim — intentionally not branded.
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--brand-primaryBackgroundColor)',
          borderRadius: '16px',
          // Elevation shadow — generic, not part of brand palette.
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          padding: '40px',
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: statusInfo.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            color: 'var(--brand-primaryTextColor)',
          }}
        >
          <Icon name={statusInfo.icon} size={32} />
        </div>

        <h2
          style={{
            fontSize: '24px',
            fontWeight: 600,
            marginBottom: '8px',
            color: 'var(--brand-primaryTextColor)',
          }}
        >
          Course {statusInfo.label}
        </h2>

        <p
          style={{
            fontSize: '16px',
            color: 'var(--brand-secondaryTextColor)',
            marginBottom: '24px',
          }}
        >
          {courseName}
        </p>

        <div
          style={{
            backgroundColor: 'var(--brand-neutralColor)',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '32px',
          }}
        >
          {hasScore && (
            <div style={{ marginBottom: '20px' }}>
              <div
                style={{
                  fontSize: '14px',
                  color: 'var(--brand-secondaryTextColor)',
                  marginBottom: '8px',
                }}
              >
                Final Score
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Progress
                  customStyles={{
                    stroke: statusInfo.color,
                    trail: 'var(--brand-borderColor)',
                  }}
                  label={formatScoreDisplay(session.score, session.scoreMin, session.scoreMax)}
                  name='finalScore'
                  percent={normalizeScore(session.score, session.scoreMin, session.scoreMax) ?? 0}
                  styleType={ProgressType.circle}
                />
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: '32px' }}>
            <div>
              <div
                style={{
                  fontSize: '14px',
                  color: 'var(--brand-secondaryTextColor)',
                  marginBottom: '4px',
                }}
              >
                Progress
              </div>
              <div
                style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: 'var(--brand-primaryTextColor)',
                }}
              >
                {session.progress}%
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: '14px',
                  color: 'var(--brand-secondaryTextColor)',
                  marginBottom: '4px',
                }}
              >
                Status
              </div>
              <div
                style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: 'var(--brand-primaryTextColor)',
                }}
              >
                {statusInfo.label}
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {canDownloadCertificate && (
            <Button
              loading={downloadingCertificate}
              name='downloadCertificate'
              onClick={handleDownloadCertificate}
            >
              {t('contentSpace.public.completion.downloadCertificate')}
            </Button>
          )}
          <Button name='closeCompletionDialog' onClick={onClose} styleType='secondary'>
            Continue Course
          </Button>
          <Link name='backToCourseList' to={portalLandingUrl}>
            <Button name='backToCourseListBtn' styleType='secondary'>
              Back to Course List
            </Button>
          </Link>
        </div>
      </div>
    </div>
  )
}
